/**
 * Что сказать юристу, когда приложение упало.
 *
 * Отдельным файлом и без React намеренно: этим же текстом пользуется последний рубеж
 * (`lastResort.ts`), который работает тогда, когда React уже мёртв или ещё не запущен.
 * Общая функция на оба случая — чтобы экран падения не разошёлся сам с собой.
 */

/** Длиннее этого на экране телефона всё равно не прочесть, а прокрутка мешает снимку. */
const LIMIT = 300;

/**
 * Короткая строка о причине падения — та, что показывается на экране.
 *
 * Показывается **целиком и как есть**: это не украшение, а единственный способ узнать
 * причину. Юрист присылает снимок экрана, и по нему видно, что случилось. Сгладить
 * текст до «что-то пошло не так» значит потерять сам смысл этого экрана.
 */
export function crashMessage(reason: unknown): string {
  const text = describe(reason).replace(/\s+/g, ' ').trim();

  if (!text) return 'Причина неизвестна: приложение не сообщило ничего.';
  return text.length > LIMIT ? `${text.slice(0, LIMIT)}…` : text;
}

function describe(reason: unknown): string {
  if (reason instanceof Error) {
    // Имя ошибки несёт половину смысла: `QuotaExceededError` и `TypeError` —
    // разные разговоры с владельцем устройства.
    return reason.name && reason.name !== 'Error' ? `${reason.name}: ${reason.message}` : reason.message;
  }
  if (typeof reason === 'string') return reason;

  /*
   * Бросить в JS можно что угодно, и на практике бросают.
   *
   * `String({})` даёт «[object Object]» — строку, которая не говорит ничего;
   * поэтому у объектов сначала спрашиваем `message`, как у ошибки.
   */
  if (reason && typeof reason === 'object') {
    const message = (reason as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;

    // Без `message` остаётся сам объект. `String` дал бы «[object Object]» — строку,
    // по которой нельзя даже понять, о чём речь; поля хотя бы называют вещи.
    try {
      const fields = JSON.stringify(reason);
      if (fields && fields !== '{}') return fields;
    } catch {
      // Круговая ссылка или `BigInt` внутри: ничего не поделать, ниже будет общий текст.
    }
    return '';
  }
  return reason === undefined || reason === null ? '' : String(reason);
}

/**
 * Строчка для снимка экрана: где искать причину.
 *
 * `iOS 18.7 · с домашнего экрана · сеть есть · 0.1.0 · 15.09.2026, 17:40`
 *
 * **Никуда не отправляется.** Её пересылает сам юрист, сняв экран, — решение владельца
 * 15.09.2026 ([CLAUDE.md](../../../CLAUDE.md) §7). Поэтому здесь ровно то, что сужает
 * поиск, и ничего сверх: ни одного названия, ни одного текста из дел.
 *
 * Каждое поле отвечает на вопрос, который иначе пришлось бы задавать письмом:
 * иконка или вкладка — это разный срок жизни хранилища на iOS и разное поведение при
 * заморозке; сеть — обрыв это или падение; версия системы — ветка OPFS или IndexedDB.
 */
export function diagnostics(version?: string): string {
  const agent = navigator.userAgent;
  const ios = /(?:iPhone|iPad|CPU) OS (\d+)[._](\d+)/.exec(agent);

  /*
   * Два способа узнать, что приложение стоит иконкой, — и нужны оба.
   *
   * `navigator.standalone` — старое свойство Safari, на iOS оно единственное надёжное;
   * `display-mode` — стандарт, который работает везде остальном. Порознь каждый
   * промахивается на своей половине устройств.
   */
  const standalone =
    (navigator as { standalone?: boolean }).standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches === true;

  return [
    ios ? `iOS ${ios[1]}.${ios[2]}` : 'система неизвестна',
    standalone ? 'с домашнего экрана' : 'вкладка браузера',
    navigator.onLine ? 'сеть есть' : 'сети нет',
    version,
    new Date().toLocaleString('ru-RU'),
  ]
    .filter(Boolean)
    .join(' · ');
}
